import React, {useEffect, useState} from "react"
import OrderService from "../../../services/order/OrderService"
import {Order, LineItem} from "../../../dtos/orderResponse"
import LineItemState from "./LineItemState"
import {getCaseId, getQueryParameters} from "../../../utils/UrlParser"
import {ButtonActionTypes, postActionLog} from "../../../services/order/TrackOrderActions"
import {Event, EventResponse} from "../../../dtos/eventResponse"
import ReplacementHistory from "./ReplacementHistory"
import AdditionalFees from "./AdditionalFees"
import Tax from "../../formatting/Tax"
import Price from "../../formatting/Price"
import DisplayProductAttributes from "./DisplayProductAttributes"
import {
	getDeliveryEventByTrackingId,
	getFulfillmentAcceptedEventsPerItem,
	getInvalidReplacementEventsPerItem,
	getItemStatus,
	getMcpItemId,
	getShippedEventByTrackingId,
} from "../../../services/order/eventHelper"
import {CommerceItem} from "../../../services/order/Order"
import CircularIndeterminate from "../../feedback/CircularIndeterminate"
import {makeStyles} from "tss-react/mui"
import {Box, Card, CardHeader, Grid, Typography} from "@mui/material"
import {useParams} from "react-router-dom"

interface LineItemInfoProps {
	customerId: string | null
}

export interface LineItemInfoRouterProps {
	orderNumber: string
	itemId: string
}
const useStyles = makeStyles()({
	cardTitle: {
		overflowWrap: "break-word",
		width: "230px",
	},
	center: {
		display: "block",
		marginLeft: "auto",
		marginRight: "auto",
		width: "250px",
		hight: "50px",
	},
})

const orderService = new OrderService()
export default function LineItemInfo(props: LineItemInfoProps) {
	const {classes} = useStyles()
	const {customerId} = props
	const params = useParams()
	const orderNumber = params.orderNumber ?? ""
	const itemId = params.itemId ?? ""
	const [order, setOrder] = useState<Order | null>(null)
	const [lineItem, setLineItem] = useState<LineItem | null>(null)
	const [eventHist, setEventHist] = useState<Event[]>([])
	const [fulfillerName, setFulfillerName] = useState("")
	const [loadingItemDetails, setLoadingItemDetails] = useState<boolean>(true)
	const [eventResponse, setEventResponse] = useState<EventResponse | null>(null)

	useEffect(() => {
		async function loadItem(orderNumber: string | null) {
			if (orderNumber) {
				const order = await orderService.getOrder(orderNumber)
				if (order) {
					setOrder(order)
					console.log("order *****:" + JSON.stringify(order))
					if (itemId) {
						const filteredLineItems = order.orderData.lineItems.filter(
							(lineItem) => lineItem.id === itemId
						)
						if (filteredLineItems.length > 0) {
							setLineItem(filteredLineItems[0])
						} else {
							console.error("Unable to find the order item")
						}
					}
				} else {
					console.error("unable to find order")
				}
			}
			setLoadingItemDetails(false)
		}
		const getFulfillerName = async () => {
			let name = "Unknown"
			const fulfillmentAcceptedEventsPerItem = getFulfillmentAcceptedEventsPerItem(eventHist)
			if (order && eventHist && eventHist.length > 0 && (!fulfillerName || fulfillerName === "")) {
				const events = fulfillmentAcceptedEventsPerItem.get(itemId)
				if (events && events.length > 0) {
					name =
						events[0].eventProperties &&
						events[0].eventProperties.details &&
						events[0].eventProperties.details.length > 0 &&
						events[0].eventProperties.details[0].additionalData &&
						events[0].eventProperties.details[0].additionalData.fulfillerName
							? events[0].eventProperties.details[0].additionalData.fulfillerName
							: "Unknown"
				} else {
					const mcpItemId = getMcpItemId(eventHist, itemId)
					const commerceOrder = await orderService.getCommerceOrder(
						mcpItemId,
						order?.orderData.orderCreationDate
					)
					if (commerceOrder && commerceOrder.items) {
						const targetItem = commerceOrder.items.find(
							(item: CommerceItem) => item.itemId === mcpItemId || item.merchantItemId === itemId
						)
						if (targetItem && targetItem.fulfillerId) {
							name = targetItem.fulfillerId === "1" ? "Vistaprint" : "3PF"
						} else {
							name = "Unknown"
						}
					} else {
						name = "Unknown"
					}
				}
			}
			return name
		}
		const loadEventHistory = async () => {
			const eventResponse = await orderService.getOrderEventsV2(orderNumber, -1, "")
			if (eventResponse) {
				setEventResponse(eventResponse)
			}
			const evHist = getOrderEvents(eventResponse)
			setEventHist(evHist)
		}
		const loadFulfillerDetails = async () => {
			const fulfillerName = await getFulfillerName()
			setFulfillerName(fulfillerName)
		}
		if (customerId && orderNumber && itemId) {
			setLoadingItemDetails(true)
			loadItem(orderNumber)
			loadEventHistory()
			loadFulfillerDetails()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [customerId, orderNumber, itemId])
	const getOrderEvents = (eventResponse: EventResponse): Event[] => {
		if (eventResponse && eventResponse.orderEvents && eventResponse.orderEvents.length > 0) {
			const events = eventResponse.orderEvents.map((orderEvent) => {
				return orderEvent.event
			})
			return events
		}
		return []
	}

	const getInvalidReplacementEvents = () => {
		const invalidReplacementEventsPerItem = getInvalidReplacementEventsPerItem(eventHist)
		return invalidReplacementEventsPerItem.get(itemId)
	}

	const getQuantityOfTrackingId = (trackingId: string): string | number => {
		const event = getShippedEventByTrackingId(trackingId, itemId, eventHist)
		return event ? event.itemInfo[0].quantity : "No data"
	}
	const getShippingDateOfTrackingId = (trackingId: string): string => {
		const event = getShippedEventByTrackingId(trackingId, itemId, eventHist)
		return event ? new Date(event.dateTime).toDateString() : "Not yet"
	}

	const getDeliveryDateOfTrackingId = (trackingId: string): string => {
		const event = getDeliveryEventByTrackingId(trackingId, itemId, eventHist)
		return event ? new Date(event.dateTime).toDateString() : ""
	}
	function documentIdIsAvailable() {
		return (
			lineItem &&
			(lineItem as any).customOrderData &&
			(((lineItem as any).customOrderData.analytics &&
				(lineItem as any).customOrderData.analytics.workId) ||
				((lineItem as any).customOrderData.fulfillment &&
					(lineItem as any).customOrderData.fulfillment.documentId))
		)
	}

	function getWorkId() {
		const queryParams = getQueryParameters((lineItem as any).editDocumentUrl)
		if (queryParams["workId"]) {
			return queryParams["workId"]
		} else {
			return (lineItem as any).customOrderData.fulfillment.documentId
				? (lineItem as any).customOrderData.fulfillment.documentId
				: (lineItem as any).customOrderData.analytics.workId
		}
	}

	function getRequestId() {
		if (lineItem && lineItem.customOrderData && (lineItem.customOrderData as any).fulfillment) {
			return (lineItem.customOrderData as any).fulfillment.RequestAltId
		} else {
			return null
		}
	}
	const logClickTrackingNumber = (trackingId: string) => {
		try {
			const caseId = getCaseId()
			postActionLog(
				{
					event: ButtonActionTypes.OpenTrackingUrl,
					actionMetaData: {
						itemId: lineItem?.id,
						orderNumber: order?.orderNumber,
						tenantId: order?.orderData.tenantId,
						trackingId,
					},
				},
				caseId
			)
		} catch (err) {
			console.log("error when sending log data ")
		}
	}
	const getItemKeyEvents = () => {
		if (eventResponse) {
			const filteredKeyEvents = eventResponse.itemEvents.filter((items) => items.itemdId === itemId)
			if (filteredKeyEvents.length > 0) {
				const keyEvents = filteredKeyEvents[0].events
				return keyEvents.map((keyEvent) => keyEvent.event)
			}
		}
		return []
	}

	return (
		<Card
			raised={true}
			style={{
				minWidth: "20%",
				maxWidth: "100%",
				minHeight: "100%",
				borderRadius: "8px",
				padding: "10px",
			}}
		>
			{loadingItemDetails ? (
				<CircularIndeterminate />
			) : order && lineItem ? (
				<div>
					<CardHeader
						titleTypographyProps={{variant: "h6", noWrap: false}}
						title={
							<div className={classes.cardTitle}>
								{lineItem?.productName ? lineItem.productName : "Unknown"}
							</div>
						}
						style={{
							minWidth: "25%",
							maxWidth: "100%",
						}}
					/>
					<div>
						<Grid container justifyContent="space-between">
							<Grid item xs={12}>
								<div style={{width: "100%"}}>
									<img src={lineItem?.livePreviewUrl} className={classes.center} alt="" />
								</div>
							</Grid>
							<Grid item xs={12}>
								<LineItemState
									itemNumber={order.orderData.lineItems.length}
									orderNumber={order.orderNumber}
									lineItem={lineItem}
									itemStatus={getItemStatus(getItemKeyEvents())}
								/>
							</Grid>
							<Grid item xs={12}>
								<Typography>
									{!lineItem.trackingData || lineItem.trackingData.length === 0 ? (
										"No tracking URL available"
									) : (
										<div>
											<b>Tracking Information: </b>
											{lineItem.trackingData.map((elt) => (
												<Typography
													style={{paddingLeft: "5px", marginBottom: "5px"}}
													variant="body1"
												>
													<a
														href={elt.trackingUrl}
														target="_blank"
														rel="noopener noreferrer"
														onClick={() => {
															logClickTrackingNumber(elt.trackingId)
														}}
													>
														{`Shipment #${lineItem.trackingData.indexOf(elt) + 1}  `}
														(QTY: {getQuantityOfTrackingId(elt.trackingId)})
													</a>{" "}
													<br />
													Shipped on <b>{getShippingDateOfTrackingId(elt.trackingId)}</b>
													<br />
													{getDeliveryDateOfTrackingId(elt.trackingId) !== "" && (
														<>
															Delivered on <b>{getDeliveryDateOfTrackingId(elt.trackingId)}</b>
														</>
													)}
												</Typography>
											))}
										</div>
									)}
								</Typography>
							</Grid>
							<Grid item xs={12}>
								<Typography>
									Expected by :{" "}
									<b>
										{(lineItem as any).promisedArrivalDate
											? (lineItem as any).promisedArrivalDate
											: "Not available"}
									</b>
								</Typography>
							</Grid>
							<Grid item xs={10}>
								<ReplacementHistory
									replacementHistory={
										lineItem.replacementHistory ? lineItem.replacementHistory : []
									}
									lastReplacementFailure={getInvalidReplacementEvents()}
								/>
							</Grid>
							<Grid item xs={2} />
							<Grid item xs={12}>
								{lineItem.corePricing && lineItem.corePricing.additionalFees && (
									<AdditionalFees
										additionalFees={lineItem.corePricing.additionalFees}
										currency={lineItem.totalListPrice.currencyCode}
										fractionDigits={lineItem.totalListPrice.fractionDigits}
									/>
								)}
							</Grid>
							<Grid item xs={10}>
								<Typography style={{fontWeight: "bold", display: "inline-block"}}>
									Item Details
								</Typography>
							</Grid>
							<Grid item xs={2} />
							<Grid container justifyContent="space-between">
								<Grid item xs={4}>
									<Typography variant="subtitle1">Quantity</Typography>
								</Grid>
								<Grid item xs={8}>
									<Typography variant="subtitle1" style={{fontWeight: 600}}>
										{lineItem.quantity}
									</Typography>
								</Grid>
								<Grid item xs={4}>
									<Typography variant="subtitle1">Price</Typography>
								</Grid>
								<Grid item xs={8}>
									<Typography
										variant="caption"
										style={{fontWeight: 300, color: "gray", fontSize: "11px"}}
									>
										<Tax
											taxedValue={
												lineItem.pricingBreakdownV3.totalDiscountedPriceCents
													? lineItem.pricingBreakdownV3.totalDiscountedPriceCents.taxed
													: 0
											}
											untaxedValue={
												lineItem.pricingBreakdownV3.totalDiscountedPriceCents
													? lineItem.pricingBreakdownV3.totalDiscountedPriceCents.untaxed
													: 0
											}
											fractionDigits={lineItem.totalListPrice.fractionDigits}
											currency={lineItem.totalListPrice.currencyCode}
										/>
									</Typography>
								</Grid>
								<br />
								<Grid item xs={4}></Grid>
								<Grid container direction="row" item xs={8}>
									{lineItem.totalListPrice.centAmount ===
									lineItem.totalDiscountedPrice.centAmount ? (
										<></>
									) : (
										<Grid item xs={4}>
											<Typography variant="subtitle1" display="block" style={{fontWeight: 600}}>
												<Price
													value={String(lineItem.totalListPrice.centAmount)}
													fractionDigits={lineItem.totalListPrice.fractionDigits}
													currency={lineItem.totalListPrice.currencyCode}
													discounted={true}
												/>
											</Typography>
										</Grid>
									)}
									<Grid item xs={8}>
										<Typography variant="subtitle1" style={{fontWeight: 600}}>
											<Price
												value={String(lineItem.totalDiscountedPrice.centAmount)}
												fractionDigits={lineItem.totalListPrice.fractionDigits}
												currency={lineItem.totalListPrice.currencyCode}
											/>
										</Typography>
									</Grid>
								</Grid>
							</Grid>
							<Grid item xs={10}>
								<DisplayProductAttributes
									displayProductAttribute={lineItem.displayProductAttributes}
									orderNumber={orderNumber}
									lineItemId={lineItem.id}
								></DisplayProductAttributes>
							</Grid>
							<Grid item xs={2} />
							{documentIdIsAvailable() && (
								<Grid item xs={12}>
									<Typography variant="subtitle1" key="workId">
										Doc ID: {getWorkId()}
									</Typography>
								</Grid>
							)}
							{lineItem.altDocId && (
								<Grid item xs={12}>
									<Typography variant="subtitle1" key="altDocId">
										Legacy Doc ID: {lineItem.altDocId}
									</Typography>
								</Grid>
							)}
							{getRequestId() && (
								<Grid item xs={12}>
									<Typography variant="subtitle1" key="requestId">
										Request ID: {getRequestId()}
									</Typography>
								</Grid>
							)}
							{lineItem.id && (
								<Grid item xs={12}>
									<Typography variant="subtitle1" key="altDocId">
										Item ID: {lineItem.id}
									</Typography>
								</Grid>
							)}
							{fulfillerName && (
								<Grid item xs={12}>
									<Typography
										style={{fontWeight: "bold", display: "inline-block"}}
										key="fulfillmentMerchantId"
									>
										Fulfiller :{" "}
										{fulfillerName.toLowerCase() !== "unknown"
											? fulfillerName.toLowerCase() === "vistaprint"
												? "In house"
												: "3rd Party Fullfiller"
											: "Fulfiller not yet assigned"}
									</Typography>
								</Grid>
							)}
						</Grid>
					</div>
				</div>
			) : (
				<Box margin="10px">
					<Typography>Order item not found</Typography>
				</Box>
			)}
		</Card>
	)
}
